type HandlerHelpers = {
  changeTagName: (el: Element, newTag: string) => Element
  wrapWith: (el: Element, wrapperTag: string) => Element
  set: (el: Element, newEl: Element) => void
}

export type HTMLProcessHandler = {
  predicate: (el: Element, prev: Element | null) => boolean
  handler: (el: Element, prev: Element | null, helpers: HandlerHelpers) => void | null
}

export function preprocessHTML(htmlStr: string, handlers: HTMLProcessHandler[]): string {
  const parser = new DOMParser();
  const doc = parser.parseFromString(htmlStr, "text/html");

  function changeTagName(element: Element, newTagName: string): Element {
    const newElement = doc.createElement(newTagName);
    const attrs = element.attributes;
    for (let i = 0; i < attrs.length; i++) {
      const attr = attrs[i];
      newElement.setAttribute(attr.name, attr.value);
    }
    while (element.firstChild) {
      newElement.appendChild(element.firstChild);
    }
    element.replaceWith(newElement);
    return newElement;
  }

  function wrapWith(element: Element, wrapperTagName: string): Element {
    const wrapper = doc.createElement(wrapperTagName);
    element.replaceWith(wrapper);
    wrapper.appendChild(element);
    return wrapper;
  }

  function set(element: Element, newElement: Element): void {
    element.replaceWith(newElement);
  }

  function traverse(parent: Element): void {
    const children = Array.from(parent.children);
    for (let i = 0; i < children.length; i++) {
      const el = children[i];
      const prev = i > 0 ? children[i - 1] : null;

      let shouldRemove = false;

      for (const { predicate, handler } of handlers) {
        if (predicate(el, prev)) {
          const result = handler(el, prev, {
            changeTagName,
            wrapWith,
            set,
          });
          if (result === null) {
            shouldRemove = true;
          }
          break;
        }
      }

      if (!shouldRemove) {
        traverse(el);
      } else {
        el.remove();
      }
    }
  }

  traverse(doc.documentElement);
  return doc.documentElement.outerHTML;
}

export function utf8ToBase64(str: string): string {
  const bytes = new TextEncoder().encode(str);
  const binaryStr = Array.from(bytes, (byte) => String.fromCharCode(byte)).join("");
  return btoa(binaryStr);
}

export function base64ToUtf8(base64: string): string {
  const binaryStr = atob(base64);
  const bytes = Uint8Array.from(binaryStr, (ch) => ch.charCodeAt(0));
  return new TextDecoder().decode(bytes);
}
